import { z } from "zod";
import { zEmail } from "./common.js";
import { parseMinorUnits } from "./currency.js";
import { DEFAULT_PERSONALIZATION, zGiftArrayMode, zGiftArrayPersonalization, type GiftArrayPersonalization } from "./giftArrays.js";
import type { PublicExperimentAssignment } from "./experiments.js";
import type { PublicMembershipRenewal } from "./memberships.js";
import { zLanguage, type PublicLanguageInfo } from "./i18n.js";

/**
 * Landing-page document: the form/landing-page builder's design contract.
 * Same philosophy as emailDoc — typed blocks the builder edits and the
 * public page renders. At most one form block and one giving block per page.
 */

export const zFormFieldType = z.enum(["text", "email", "phone", "textarea", "select", "checkbox"]);

/**
 * Form types (CT-RM-12c). "contact" is the original free-form form; the others
 * route a submission into an existing module:
 *   lead       → constituent + tag
 *   pledge     → pledge with installments (PledgesService)
 *   event      → event registration (EventsService public registration)
 *   volunteer  → shift signup (VolunteersService)
 *   membership → join or renew (MembershipsService) — test mode, no payment
 * Every type also records a form_submission and fires form_submitted.
 */
export const FORM_PURPOSES = ["contact", "lead", "pledge", "event", "volunteer", "membership"] as const;
export const zFormPurpose = z.enum(FORM_PURPOSES);
export type FormPurpose = z.infer<typeof zFormPurpose>;

/**
 * What a field means to the form type. Role fields are preset by the type and
 * read by the server; options of the "dynamic" roles (ticket, shift, level)
 * are filled from live records when the public page is served.
 */
export const FORM_FIELD_ROLES = [
  "firstName",
  "lastName",
  "email",
  "phone",
  "pledgeAmount",
  "pledgeFrequency",
  "pledgeInstallments",
  "eventTicket",
  "volunteerShift",
  "membershipLevel",
] as const;
export const zFormFieldRole = z.enum(FORM_FIELD_ROLES);
export type FormFieldRole = z.infer<typeof zFormFieldRole>;
export const DYNAMIC_OPTION_ROLES: readonly FormFieldRole[] = ["eventTicket", "volunteerShift", "membershipLevel"];

export const zFormField = z.object({
  id: z.string().max(20),
  type: zFormFieldType,
  label: z.string().max(200),
  required: z.boolean().default(false),
  /** for select */
  options: z.array(z.string().max(120)).max(25).default([]),
  /** maps this field onto the constituent record (first/last/email/phone are auto-mapped by type/name) */
  placeholder: z.string().max(200).default(""),
  /** set by form-type presets; see FORM_FIELD_ROLES */
  role: zFormFieldRole.optional(),
});
export type FormField = z.infer<typeof zFormField>;

/** which gift totals a progress/donor-wall block reads: the page's campaign or its fund */
export const zWidgetBasis = z.enum(["campaign", "fund"]).default("campaign");
export type WidgetBasis = z.infer<typeof zWidgetBasis>;
export const MAX_DONOR_WALL = 50;

/**
 * CT-RM-31 cart giving: "Multiple designations" on a giving block. The donor
 * builds several fund lines, each with its own amount and frequency, and
 * checks out once. Absent / enabled:false = the classic single-designation
 * block (one amount, the page's fund, the monthly toggle).
 */
export const MAX_CART_LINES = 10;
export const MAX_CART_FUNDS = 20;
export const CART_FREQUENCIES = ["once", "monthly"] as const;
export const zCartFrequency = z.enum(CART_FREQUENCIES);
export type CartFrequency = z.infer<typeof zCartFrequency>;

export const zGivingCart = z.object({
  enabled: z.boolean().default(false),
  /** funds offered on each line, in display order; empty = every active fund */
  fundIds: z.array(z.string().uuid()).max(MAX_CART_FUNDS).default([]),
  minLines: z.number().int().min(1).max(MAX_CART_LINES).default(1),
  maxLines: z.number().int().min(1).max(MAX_CART_LINES).default(5),
  /** the fund of the line a new cart starts with; null = the first offered fund */
  defaultFundId: z.string().uuid().nullable().default(null),
  /** lines may be monthly (recurring) as well as one-time */
  allowRecurring: z.boolean().default(true),
  /** offer "cover the fees" once, on the cart total */
  offerFeeCover: z.boolean().default(true),
});
export type GivingCart = z.infer<typeof zGivingCart>;

export const DEFAULT_GIVING_CART: GivingCart = {
  enabled: false,
  fundIds: [],
  minLines: 1,
  maxLines: 5,
  defaultFundId: null,
  allowRecurring: true,
  offerFeeCover: true,
};

export const zPageBlock = z.discriminatedUnion("type", [
  z.object({
    id: z.string(),
    type: z.literal("hero"),
    heading: z.string().max(300),
    subheading: z.string().max(500).default(""),
    align: z.enum(["left", "center"]).default("center"),
  }),
  z.object({
    id: z.string(),
    type: z.literal("heading"),
    text: z.string().max(300),
    level: z.union([z.literal(1), z.literal(2)]).default(2),
    align: z.enum(["left", "center"]).default("left"),
  }),
  z.object({
    id: z.string(),
    type: z.literal("text"),
    text: z.string().max(20_000),
    align: z.enum(["left", "center"]).default("left"),
  }),
  z.object({
    id: z.string(),
    type: z.literal("image"),
    src: z.string().max(1000),
    alt: z.string().max(300).default(""),
    width: z.number().int().min(10).max(100).default(100),
    rounded: z.boolean().default(true),
  }),
  z.object({
    id: z.string(),
    type: z.literal("button"),
    label: z.string().max(120),
    url: z.string().max(1000),
    align: z.enum(["left", "center"]).default("center"),
  }),
  z.object({
    id: z.string(),
    type: z.literal("divider"),
  }),
  z.object({
    id: z.string(),
    type: z.literal("spacer"),
    height: z.number().int().min(4).max(160).default(24),
  }),
  z.object({
    id: z.string(),
    type: z.literal("stats"),
    items: z.array(z.object({ value: z.string().max(40), label: z.string().max(120) })).min(1).max(4),
  }),
  z.object({
    id: z.string(),
    type: z.literal("form"),
    title: z.string().max(200).default(""),
    fields: z.array(zFormField).min(1).max(20),
    submitLabel: z.string().max(80).default("Submit"),
    successMessage: z.string().max(500).default("Thank you — we've received your submission."),
    /** form type; absent = "contact" (pages saved before form types) */
    purpose: zFormPurpose.optional(),
    /** lead: tag added to the constituent */
    tag: z.string().trim().max(100).optional(),
    /** event: the (published) event registrations go to */
    eventId: z.string().uuid().nullable().optional(),
    /** volunteer: the opportunity whose upcoming shifts are offered */
    opportunityId: z.string().uuid().nullable().optional(),
    /** membership: the program whose levels are offered */
    programId: z.string().uuid().nullable().optional(),
  }),
  z.object({
    id: z.string(),
    type: z.literal("giving"),
    title: z.string().max(200).default("Make a gift"),
    presets: z.array(z.number().int().min(100).max(100_000_000)).min(1).max(6),
    allowCustom: z.boolean().default(true),
    allowMonthly: z.boolean().default(true),
    buttonLabel: z.string().max(80).default("Give now"),
    /**
     * CT-RM-13: "personalized" shows a known donor (portal session or signed
     * ?d= link) an array computed server-side from their history; everyone
     * else sees `presets`. Absent = fixed (pages saved before 13).
     */
    arrayMode: zGiftArrayMode.optional(),
    personalization: zGiftArrayPersonalization.optional(),
    /** CT-RM-31: multiple designations (cart); absent = single designation */
    cart: zGivingCart.optional(),
  }),
  /**
   * Fundraising progress (CT-RM-12b): raised vs goal for the page's campaign
   * or fund, from live gift totals. Campaign basis defaults the goal to the
   * campaign's own goal; a fund has no stored goal, so the block sets one.
   */
  z.object({
    id: z.string(),
    type: z.literal("progress"),
    title: z.string().max(200).default("Our progress"),
    basis: zWidgetBasis,
    /** minor units; null = use the campaign's goal */
    goal: z.number().int().min(100).max(100_000_000_000).nullable().default(null),
    showDonorCount: z.boolean().default(true),
  }),
  /**
   * Donor wall (CT-RM-12b): recent donors to the page's campaign or fund,
   * first name + last initial only. Anonymous gifts and donors who opted out
   * of public recognition are never listed. Amounts are never shown.
   */
  z.object({
    id: z.string(),
    type: z.literal("donor_wall"),
    title: z.string().max(200).default("Thank you to our donors"),
    basis: zWidgetBasis,
    limit: z.number().int().min(3).max(MAX_DONOR_WALL).default(12),
  }),
]);
export type PageBlock = z.infer<typeof zPageBlock>;
export type ProgressBlock = Extract<PageBlock, { type: "progress" }>;
export type DonorWallBlock = Extract<PageBlock, { type: "donor_wall" }>;

export const zPageStyles = z.object({
  /** curated display/body font pairing (the web app maps keys to families) */
  fontPair: z.enum(["editorial", "classic", "modern", "bold", "warm"]).default("editorial"),
  accentColor: z.string().max(30).default("#2d6a4f"),
  backgroundColor: z.string().max(30).default("#faf7f2"),
  contentColor: z.string().max(30).default("#ffffff"),
  textColor: z.string().max(30).default("#26241f"),
  /** hero band background */
  heroColor: z.string().max(30).default("#2d6a4f"),
});
export type PageStyles = z.infer<typeof zPageStyles>;

/**
 * Multi-step forms: an ordered list of steps over the page's form + giving
 * blocks. Empty = classic single-page form. A step is one of:
 *   amount — the giving block (preset/custom amount, monthly toggle)
 *   fields — a subset of the form block's fields, in the listed order
 *   review — read-only summary before submitting (must be last)
 * Every form field must be assigned to exactly one "fields" step so no
 * required field is ever unreachable.
 */
export const zFormStepKind = z.enum(["amount", "fields", "review"]);
export type FormStepKind = z.infer<typeof zFormStepKind>;

export const zFormStep = z.object({
  id: z.string().min(1).max(20),
  title: z.string().trim().min(1).max(120),
  kind: zFormStepKind.default("fields"),
  fieldIds: z.array(z.string().max(20)).max(20).default([]),
});
export type FormStep = z.infer<typeof zFormStep>;

export const MAX_FORM_STEPS = 8;

export const zPageDoc = z.object({
  version: z.literal(1),
  styles: zPageStyles,
  blocks: z.array(zPageBlock).max(40),
  steps: z.array(zFormStep).max(MAX_FORM_STEPS).default([]),
});
export type PageDoc = z.infer<typeof zPageDoc>;

type FormBlock = Extract<PageBlock, { type: "form" }>;
type GivingBlock = Extract<PageBlock, { type: "giving" }>;

export type { GivingBlock };

/** a giving block's personalization settings with defaults filled in */
export function personalizationOf(block: Pick<GivingBlock, "personalization">): GiftArrayPersonalization {
  return { ...DEFAULT_PERSONALIZATION, ...block.personalization };
}

export function formBlockOf(doc: Pick<PageDoc, "blocks">): FormBlock | undefined {
  return doc.blocks.find((b): b is FormBlock => b.type === "form");
}
export function givingBlockOf(doc: Pick<PageDoc, "blocks">): GivingBlock | undefined {
  return doc.blocks.find((b): b is GivingBlock => b.type === "giving");
}

// ---- cart giving (CT-RM-31) ----

/** the giving block's cart settings with defaults, or null when the block is single-designation */
export function cartOf(block: Pick<GivingBlock, "cart"> | undefined | null): GivingCart | null {
  if (!block?.cart?.enabled) return null;
  return { ...DEFAULT_GIVING_CART, ...block.cart };
}

export const zCartLine = z.object({
  fundId: z.string().uuid(),
  /** minor units */
  amount: z.number().int().min(100).max(100_000_000),
  frequency: zCartFrequency.default("once"),
});
export type CartLine = z.infer<typeof zCartLine>;
/** a line being edited in the renderer: amount not chosen yet = null */
export type CartDraftLine = { key: string; fundId: string; amount: number | null; frequency: CartFrequency };

/** a fund a cart line can be designated to (served with the public page) */
export interface PublicCartFund {
  id: string;
  name: string;
}

/** Builder/API: problems with a giving block's cart settings, or [] when valid. */
export function givingCartErrors(block: Pick<GivingBlock, "cart">): string[] {
  const cart = block.cart;
  if (!cart?.enabled) return [];
  const errors: string[] = [];
  if (cart.minLines > cart.maxLines) errors.push("Minimum lines can't be more than maximum lines");
  if (new Set(cart.fundIds).size !== cart.fundIds.length) errors.push("A fund is offered twice");
  if (cart.defaultFundId && cart.fundIds.length && !cart.fundIds.includes(cart.defaultFundId)) errors.push("The default line's fund must be one of the offered funds");
  return errors;
}

/**
 * Fee model for "cover the fees" (2.9% + 30¢), shared by the hosted giving
 * page and the cart: the amount to charge so `amount` arrives after fees.
 */
export function grossUpForFees(amount: number): number {
  return Math.round((amount + 30) / (1 - 0.029));
}
export function feeCoverFor(amount: number): number {
  return amount > 0 ? grossUpForFees(amount) - amount : 0;
}

/**
 * The cart's fee cover (computed once, on the total) spread over its lines in
 * proportion to their amounts — largest remainder, so the shares sum exactly.
 */
export function allocateFeeCover(amounts: number[], fee: number): number[] {
  const total = amounts.reduce((s, a) => s + a, 0);
  if (!fee || !total) return amounts.map(() => 0);
  const raw = amounts.map((a) => (fee * a) / total);
  const shares = raw.map(Math.floor);
  const left = fee - shares.reduce((s, a) => s + a, 0);
  const order = raw.map((r, i) => ({ i, rem: r - Math.floor(r) })).sort((a, b) => b.rem - a.rem || a.i - b.i);
  for (const { i: idx } of order.slice(0, left)) shares[idx] = shares[idx]! + 1;
  return shares;
}

/** cart totals: lines, the single fee cover, and what is charged today / per month */
export function cartTotals(lines: Pick<CartLine, "amount" | "frequency">[], coverFees: boolean) {
  const subtotal = lines.reduce((s, l) => s + l.amount, 0);
  const fee = coverFees ? feeCoverFor(subtotal) : 0;
  const shares = allocateFeeCover(lines.map((l) => l.amount), fee);
  const charged = lines.map((l, i) => l.amount + shares[i]!);
  const monthly = lines.reduce((s, l, i) => s + (l.frequency === "monthly" ? charged[i]! : 0), 0);
  return { subtotal, fee, total: subtotal + fee, shares, charged, monthly };
}

/**
 * Cart rules shared by the renderer (before Next / submit) and the API: line
 * count within min/max, every line on an offered fund with an amount, monthly
 * only when recurring lines are allowed. `offered` = the fund ids served.
 */
export function cartLineErrors(cart: GivingCart, lines: Pick<CartDraftLine, "fundId" | "amount" | "frequency">[], offered: readonly string[]): string[] {
  const errors: string[] = [];
  if (lines.length < cart.minLines) errors.push(cart.minLines === 1 ? "Add at least one gift" : `Add at least ${cart.minLines} gifts`);
  if (lines.length > cart.maxLines) errors.push(`You can add up to ${cart.maxLines} gifts`);
  lines.forEach((l, i) => {
    const n = lines.length > 1 ? ` (gift ${i + 1})` : "";
    if (!offered.includes(l.fundId)) errors.push(`Choose a fund${n}`);
    if (l.amount == null || !Number.isInteger(l.amount) || l.amount < 100) errors.push(`Choose or enter an amount of at least 1.00${n}`);
    else if (l.amount > 100_000_000) errors.push(`That amount is too large for an online gift${n}`);
    if (l.frequency === "monthly" && !cart.allowRecurring) errors.push(`Monthly gifts aren't offered on this form${n}`);
  });
  return errors;
}

// ---- form types (CT-RM-12c) ----

export function formPurposeOf(form: Pick<FormBlock, "purpose"> | undefined): FormPurpose {
  return form?.purpose ?? "contact";
}

export function fieldByRole(form: Pick<FormBlock, "fields"> | undefined, role: FormFieldRole): FormField | undefined {
  return form?.fields.find((f) => f.role === role);
}

export const FORM_PURPOSE_LABELS: Record<FormPurpose, { label: string; hint: string }> = {
  contact: { label: "Contact form", hint: "Custom fields → constituent" },
  lead: { label: "Lead / interest", hint: "Constituent + tag" },
  pledge: { label: "Pledge", hint: "Pledge with an installment schedule" },
  event: { label: "Event registration", hint: "Registers for a published event" },
  volunteer: { label: "Volunteer signup", hint: "Signs up for a volunteer shift" },
  membership: { label: "Membership join / renew", hint: "Level picker, test mode" },
};

/** pledge schedule options, as shown on the form → pledge frequency */
export const PLEDGE_SCHEDULES = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "semiannual", label: "Twice a year" },
  { value: "annual", label: "Yearly" },
] as const;
export type PledgeScheduleValue = (typeof PLEDGE_SCHEDULES)[number]["value"];
export const PLEDGE_INSTALLMENT_OPTIONS = ["2", "3", "4", "6", "12", "24"];
export const MAX_FORM_PLEDGE_INSTALLMENTS = 60;

export function pledgeScheduleFromLabel(label: string): PledgeScheduleValue | null {
  return PLEDGE_SCHEDULES.find((s) => s.label === label)?.value ?? null;
}

/** "1,200", "$1,200.50" → minor units; null when unparseable */
export function parseAmountAnswer(raw: string): number | null {
  return parseMinorUnits(raw.trim().replace(/^[^\d]+/, ""), 2);
}

/** Roles each type must carry as required fields — the builder locks them, the API enforces them. */
export const REQUIRED_ROLES: Record<FormPurpose, FormFieldRole[]> = {
  contact: [],
  lead: ["email"],
  pledge: ["firstName", "lastName", "email", "pledgeAmount", "pledgeFrequency", "pledgeInstallments"],
  event: ["firstName", "lastName", "email"],
  volunteer: ["firstName", "lastName", "email", "volunteerShift"],
  membership: ["firstName", "lastName", "email", "membershipLevel"],
};

/** Roles a type may use (required ones plus optional extras). */
const ALLOWED_ROLES: Record<FormPurpose, FormFieldRole[]> = {
  contact: ["firstName", "lastName", "email", "phone"],
  lead: ["firstName", "lastName", "email", "phone"],
  pledge: ["firstName", "lastName", "email", "phone", "pledgeAmount", "pledgeFrequency", "pledgeInstallments"],
  event: ["firstName", "lastName", "email", "phone", "eventTicket"],
  volunteer: ["firstName", "lastName", "email", "phone", "volunteerShift"],
  membership: ["firstName", "lastName", "email", "phone", "membershipLevel"],
};

const ROLE_TYPE: Record<FormFieldRole, FormField["type"]> = {
  firstName: "text",
  lastName: "text",
  email: "email",
  phone: "phone",
  pledgeAmount: "text",
  pledgeFrequency: "select",
  pledgeInstallments: "select",
  eventTicket: "select",
  volunteerShift: "select",
  membershipLevel: "select",
};

/** Paid tickets and membership dues are only accepted as test-mode submissions in this release. */
export function purposeUsesTestMode(purpose: FormPurpose): boolean {
  return purpose === "event" || purpose === "membership";
}

const roleField = (role: FormFieldRole, label: string, required: boolean, extra: Partial<FormField> = {}): FormField => ({
  id: role,
  type: ROLE_TYPE[role],
  label,
  required,
  options: [],
  placeholder: "",
  role,
  ...extra,
});

/**
 * Starting form block for a type: its required fields (ids = role names, so a
 * page's role fields are stable), labels and copy. Type-specific targets
 * (event, opportunity, program) are chosen in the builder afterwards.
 */
export function formPreset(purpose: FormPurpose): Omit<FormBlock, "id" | "type"> {
  const name = [roleField("firstName", "First name", true), roleField("lastName", "Last name", true)];
  const email = roleField("email", "Email", true, { placeholder: "you@example.org" });
  switch (purpose) {
    case "contact":
      return { purpose, title: "Stay in touch", fields: [...name, email], submitLabel: "Sign up", successMessage: "Thank you — we've received your submission." };
    case "lead":
      return {
        purpose,
        tag: "Interested",
        title: "Tell us what you care about",
        fields: [
          roleField("firstName", "First name", false),
          roleField("lastName", "Last name", false),
          email,
          roleField("phone", "Phone", false),
          { id: "interest", type: "textarea", label: "What are you interested in?", required: false, options: [], placeholder: "" },
        ],
        submitLabel: "Keep me posted",
        successMessage: "Thanks — we'll be in touch.",
      };
    case "pledge":
      return {
        purpose,
        title: "Make a pledge",
        fields: [
          ...name,
          email,
          roleField("pledgeAmount", "Total pledge amount", true, { placeholder: "1,200.00" }),
          roleField("pledgeFrequency", "Payment schedule", true, { options: PLEDGE_SCHEDULES.map((s) => s.label) }),
          roleField("pledgeInstallments", "Number of installments", true, { options: [...PLEDGE_INSTALLMENT_OPTIONS] }),
        ],
        submitLabel: "Make my pledge",
        successMessage: "Thank you for your pledge — we'll send a schedule of reminders.",
      };
    case "event":
      return {
        purpose,
        eventId: null,
        title: "Register",
        fields: [...name, email, roleField("eventTicket", "Ticket", false)],
        submitLabel: "Register",
        successMessage: "You're registered — see you there.",
      };
    case "volunteer":
      return {
        purpose,
        opportunityId: null,
        title: "Volunteer with us",
        fields: [...name, email, roleField("phone", "Phone", false), roleField("volunteerShift", "Shift", true)],
        submitLabel: "Sign me up",
        successMessage: "Thanks for signing up to volunteer!",
      };
    case "membership":
      return {
        purpose,
        programId: null,
        title: "Become a member",
        fields: [...name, email, roleField("membershipLevel", "Membership level", true)],
        submitLabel: "Join",
        successMessage: "Welcome — your membership is confirmed.",
      };
  }
}

/**
 * Form-type problems with a page, or [] when valid. Shared by the builder
 * (inline warnings) and the API (rejects the save).
 */
export function formPurposeErrors(doc: Pick<PageDoc, "blocks">): string[] {
  const form = formBlockOf(doc);
  if (!form) return [];
  const purpose = formPurposeOf(form);
  const errors: string[] = [];
  const label = FORM_PURPOSE_LABELS[purpose].label;
  const allowed = new Set(ALLOWED_ROLES[purpose]);
  const seen = new Set<FormFieldRole>();
  for (const f of form.fields) {
    if (!f.role) continue;
    if (seen.has(f.role)) errors.push(`Only one "${f.role}" field is allowed`);
    seen.add(f.role);
    if (!allowed.has(f.role)) errors.push(`"${f.label}" doesn't belong on a ${label.toLowerCase()}`);
    else if (f.type !== ROLE_TYPE[f.role]) errors.push(`"${f.label}" must be a ${ROLE_TYPE[f.role]} field`);
  }
  for (const role of REQUIRED_ROLES[purpose]) {
    const f = fieldByRole(form, role);
    if (!f) errors.push(`A ${label.toLowerCase()} needs its ${role} field`);
    else if (!f.required) errors.push(`"${f.label}" must be required on a ${label.toLowerCase()}`);
  }
  if (purpose !== "contact" && purpose !== "lead" && givingBlockOf(doc)) {
    errors.push(`A ${label.toLowerCase()} can't share a page with a giving block`);
  }
  if (purpose === "lead" && !form.tag?.trim()) errors.push("Choose a tag for new leads");
  if (purpose === "event" && !form.eventId) errors.push("Choose the event people register for");
  if (purpose === "volunteer" && !form.opportunityId) errors.push("Choose the volunteer opportunity");
  if (purpose === "membership" && !form.programId) errors.push("Choose the membership program");
  const freq = fieldByRole(form, "pledgeFrequency");
  if (freq && (!freq.options.length || freq.options.some((o) => !pledgeScheduleFromLabel(o)))) {
    errors.push(`"${freq.label}" options must come from: ${PLEDGE_SCHEDULES.map((s) => s.label).join(", ")}`);
  }
  const count = fieldByRole(form, "pledgeInstallments");
  if (count && (!count.options.length || count.options.some((o) => !/^\d+$/.test(o) || Number(o) < 1 || Number(o) > MAX_FORM_PLEDGE_INSTALLMENTS))) {
    errors.push(`"${count.label}" options must be whole numbers from 1 to ${MAX_FORM_PLEDGE_INSTALLMENTS}`);
  }
  return errors;
}

/**
 * Structural problems with a page's steps, or [] when valid. Shared by the
 * builder (inline warnings) and the API (rejects the save).
 */
export function stepStructureErrors(doc: Pick<PageDoc, "blocks" | "steps">): string[] {
  const steps = doc.steps ?? [];
  if (!steps.length) return [];
  const errors: string[] = [];
  const form = formBlockOf(doc);
  const giving = givingBlockOf(doc);
  if (steps.length < 2) errors.push("A multi-step form needs at least two steps");
  const ids = new Set<string>();
  for (const s of steps) {
    if (ids.has(s.id)) errors.push(`Duplicate step id "${s.id}"`);
    ids.add(s.id);
  }
  const amountSteps = steps.filter((s) => s.kind === "amount");
  const reviewSteps = steps.filter((s) => s.kind === "review");
  if (amountSteps.length > 1) errors.push("Only one amount step is allowed");
  if (amountSteps.length && !giving) errors.push("An amount step needs a giving block on the page");
  if (giving && !amountSteps.length) errors.push("Add an amount step for the giving block");
  if (reviewSteps.length > 1) errors.push("Only one review step is allowed");
  if (reviewSteps.length && steps[steps.length - 1]!.kind !== "review") errors.push("The review step must be last");

  const fieldIds = new Set(form?.fields.map((f) => f.id) ?? []);
  const assigned = new Map<string, string>();
  for (const s of steps) {
    if (s.kind !== "fields" && s.fieldIds.length) errors.push(`"${s.title}" can't hold fields (${s.kind} step)`);
    if (s.kind !== "fields") continue;
    if (!s.fieldIds.length) errors.push(`"${s.title}" has no fields`);
    for (const fid of s.fieldIds) {
      if (!fieldIds.has(fid)) errors.push(`"${s.title}" references a field that isn't on the form`);
      else if (assigned.has(fid)) errors.push(`A field is assigned to both "${assigned.get(fid)}" and "${s.title}"`);
      else assigned.set(fid, s.title);
    }
  }
  for (const f of form?.fields ?? []) {
    if (!assigned.has(f.id)) errors.push(`"${f.label}" isn't assigned to a step`);
  }
  return errors;
}

export type StepAnswers = Record<string, string | boolean | undefined>;
export const STEP_AMOUNT_KEY = "__amount";

/**
 * Per-step answer validation: returns { fieldId | STEP_AMOUNT_KEY: message }.
 * The public renderer runs this before Next; the API runs it over every step
 * on submit, so a hand-crafted POST can't skip a step's required fields.
 */
export function validateStep(
  doc: Pick<PageDoc, "blocks">,
  step: Pick<FormStep, "kind" | "fieldIds">,
  answers: StepAnswers,
  donationAmount?: number | null,
  /** CT-RM-31: a cart giving block's lines + the fund ids served; the amount step validates the cart instead */
  cart?: { lines: Pick<CartDraftLine, "fundId" | "amount" | "frequency">[]; offered: readonly string[] } | null,
): Record<string, string> {
  const errors: Record<string, string> = {};
  const cartSettings = cart ? cartOf(givingBlockOf(doc)) : null;
  if (step.kind === "amount" && cart && cartSettings) {
    cartLineErrors(cartSettings, cart.lines, cart.offered).forEach((m, i) => (errors[i ? `${STEP_AMOUNT_KEY}.${i}` : STEP_AMOUNT_KEY] = m));
    return errors;
  }
  if (step.kind === "amount") {
    if (donationAmount == null || !Number.isInteger(donationAmount) || donationAmount < 100) {
      errors[STEP_AMOUNT_KEY] = "Choose or enter an amount of at least 1.00";
    } else if (donationAmount > 100_000_000) {
      errors[STEP_AMOUNT_KEY] = "That amount is too large for an online gift";
    }
    return errors;
  }
  if (step.kind !== "fields") return errors;
  const form = formBlockOf(doc);
  for (const fid of step.fieldIds) {
    const field = form?.fields.find((f) => f.id === fid);
    if (!field) continue;
    const value = answers[fid];
    const empty = value == null || value === "" || value === false || (typeof value === "string" && !value.trim());
    if (field.required && empty) {
      errors[fid] = `${field.label} is required`;
      continue;
    }
    if (empty || typeof value !== "string") continue;
    if (field.type === "email" && !zEmail.safeParse(value.trim()).success) errors[fid] = `${field.label} must be a valid email address`;
    else if (field.type === "phone" && !/^[+()\d\s.-]{7,25}$/.test(value.trim())) errors[fid] = `${field.label} must be a valid phone number`;
    else if (field.type === "select" && field.options.length && !field.options.includes(value)) errors[fid] = `Choose an option for ${field.label}`;
    else if (field.role === "pledgeAmount") {
      const cents = parseAmountAnswer(value);
      if (cents == null || cents < 100) errors[fid] = `${field.label} must be an amount of at least 1.00`;
      else if (cents > 100_000_000) errors[fid] = `${field.label} is too large for an online pledge`;
    }
  }
  return errors;
}

/** Every field of the page's form as one pseudo-step — single-page forms validate through the same rules. */
export function validateAllFields(doc: Pick<PageDoc, "blocks">, answers: StepAnswers): Record<string, string> {
  const form = formBlockOf(doc);
  if (!form) return {};
  return validateStep(doc, { kind: "fields", fieldIds: form.fields.map((f) => f.id) }, answers);
}

export const zSavePage = z.object({
  name: z.string().trim().min(1).max(200),
  slug: z
    .string()
    .trim()
    .min(2)
    .max(80)
    .regex(/^[a-z0-9-]+$/, "lowercase letters, numbers, and dashes only"),
  design: zPageDoc,
  /** gifts from the giving block land here */
  fundId: z.string().uuid().nullable().optional(),
  campaignId: z.string().uuid().nullable().optional(),
});
export type SavePage = z.infer<typeof zSavePage>;

/** public submission payload — answers validated server-side against the page's fields */
export const zPageSubmission = z.object({
  /** CT-RM-30: the language the visitor saw; a new constituent gets it as their preferred language */
  language: zLanguage.optional(),
  answers: z.record(z.union([z.string().max(5000), z.boolean()])).default({}),
  email: zEmail.optional(),
  donation: z
    .object({
      amount: z.number().int().min(100).max(100_000_000),
      monthly: z.boolean().default(false),
      paymentToken: z.string().max(500),
      /** donor asked not to be publicly recognised (kept off donor walls) */
      anonymous: z.boolean().default(false),
    })
    .optional(),
  /**
   * CT-RM-31: a cart checkout (multiple designations) instead of `donation`.
   * One-time lines become gifts and monthly lines recurring gifts, all tied to
   * this submission and one combined receipt; fee cover applies to the total.
   */
  cart: z
    .object({
      lines: z.array(zCartLine).min(1).max(MAX_CART_LINES),
      coverFees: z.boolean().default(false),
      paymentToken: z.string().max(500),
      anonymous: z.boolean().default(false),
      /** the started cart (abandonment tracking) this checkout completes */
      cartId: z.string().uuid().optional(),
    })
    .optional(),
  /** channel attribution (e.g. arrived via an SMS keyword link, or an embedded form) */
  source: z
    .discriminatedUnion("channel", [
      z.object({
        channel: z.literal("sms"),
        keywordId: z.string().uuid().nullable().default(null),
      }),
      z.object({ channel: z.literal("embed") }),
    ])
    .optional(),
  /**
   * Test-mode submission: no payment provider is called, the receipt is
   * prepared for print (never emailed). Embedded forms always submit in test
   * mode in this slice; the API rejects embedded donations without it.
   */
  testMode: z.boolean().default(false),
  /**
   * CT-RM-17: the signed ?d= donor link the visitor arrived with. On a
   * membership form it identifies the member being renewed (instead of an
   * email match); other form types ignore it.
   */
  donorToken: z.string().max(64).optional(),
});
export type PageSubmission = z.infer<typeof zPageSubmission>;

/**
 * CT-RM-31: POST /public/pages/:org/:page/cart — a donor started a cart and
 * gave their email. Recorded as a "started" form submission; one that isn't
 * completed within CART_ABANDON_AFTER_MINUTES becomes "abandoned".
 */
export const CART_ABANDON_AFTER_MINUTES = 30;
export const zCartProgress = z.object({
  cartId: z.string().uuid().optional(),
  email: zEmail,
  language: zLanguage.optional(),
  lines: z.array(zCartLine).min(1).max(MAX_CART_LINES),
  coverFees: z.boolean().default(false),
  source: z.object({ channel: z.literal("embed") }).optional(),
  testMode: z.boolean().default(false),
});
export type CartProgress = z.infer<typeof zCartProgress>;

/** what a cart submission stores (form_submissions.cart) */
export interface StoredCart {
  lines: (CartLine & { fundName?: string; feeShare?: number; charged?: number; giftId?: string | null; recurringGiftId?: string | null })[];
  coverFees: boolean;
  fee: number;
  total: number;
  testMode: boolean;
  /** donation_abandoned, previewed in test mode: what each automation would do (nothing is sent) */
  abandonment?: { at: string; automations: { automationId: string; name: string; conditionsMatched: boolean; steps: { type: string; outcome: string; detail: string }[] }[]; note?: string };
}

/** staff list of a page's started / abandoned carts */
export interface CartSubmissionRow {
  id: string;
  status: "started" | "abandoned" | "completed";
  email: string | null;
  constituentId: string | null;
  total: number;
  lines: number;
  testMode: boolean;
  updatedAt: string;
  abandonedAt: string | null;
}

/** GET /public/pages/:org/:page — the page as this visitor sees it */
export interface PublicLandingPage extends PublicLanguageInfo {
  organizationName: string;
  /** CT-RM-30: the org's currency, for locale-aware amounts */
  currency?: string;
  id: string;
  name: string;
  /** with the visitor's A/B variant and personalized gift array already applied */
  design: PageDoc;
  /** the giving block shows this donor's personalized array */
  personalized: boolean;
  /** the visitor's A/B test assignment, for the impression beacon */
  experiment: PublicExperimentAssignment | null;
  /**
   * CT-RM-17: a membership form opened through a member's signed link — their
   * current level to preselect and the term being renewed. Never name/email.
   */
  membershipRenewal?: PublicMembershipRenewal | null;
  /** CT-RM-31: funds a cart giving block offers (absent without a cart) */
  cartFunds?: PublicCartFund[];
}

/** GET /public/embed/:org/:page — chrome-less render payload for embedded forms */
export interface PublicEmbedPage extends PublicLanguageInfo {
  organizationName: string;
  id: string;
  name: string;
  design: PageDoc;
  personalized: boolean;
  testMode: true;
  cartFunds?: PublicCartFund[];
}

/**
 * Public widget payloads (GET /public/widgets/:org/:page/progress|donor-wall).
 * Aggregates and consented first-name + last-initial only — no ids, emails,
 * amounts per donor, or anything else from the constituent record.
 */
export interface PublicProgressWidget {
  title: string;
  currency: string;
  /** minor units */
  raised: number;
  /** minor units; null when no goal is configured */
  goal: number | null;
  /** 0–100, rounded down; null without a goal */
  percent: number | null;
  /** distinct donors; null when the block hides it */
  donorCount: number | null;
  styles: PageStyles;
}

export interface PublicDonorWallEntry {
  /** "Ada L." — or an organization's name */
  name: string;
  /** ISO date of their most recent gift */
  date: string;
}

export interface PublicDonorWallWidget {
  title: string;
  donors: PublicDonorWallEntry[];
  styles: PageStyles;
}

/** The only public form of a donor's name: first name + last initial (organizations: their name). */
export function donorWallName(d: { type: string; firstName: string | null; lastName: string | null; nickname?: string | null; orgName: string | null }): string | null {
  if (d.type === "organization" || d.type === "foundation") return d.orgName?.trim() || null;
  if (d.type !== "individual") return null;
  const first = (d.firstName ?? "").trim().split(/\s+/)[0] ?? "";
  if (!first) return null;
  const initial = (d.lastName ?? "").trim().charAt(0).toUpperCase();
  return initial ? `${first} ${initial}.` : first;
}
